import axios from 'axios'

import { ApiBase } from '@/api/api'

import { getSentryHeaders } from './sentry'

interface UploadResponse {
  upload_session_id?: string
  [key: string]: any
}

type UploadInChunksProps = {
  file: File
  responseId: string
  questionId?: string | number
  inputFieldId?: string
  mode: string
  endpoint: string
  headers?: Record<string, string>
  extraFormData?: Record<string, string>
  onComplete?: ((data: any) => void) | null
}

// Common chunked upload utility function
export async function uploadInChunks({
  file,
  responseId,
  questionId,
  inputFieldId,
  mode,
  endpoint,
  headers = {},
  extraFormData = {},
  onComplete = null,
}: UploadInChunksProps) {
  const chunkSize = 1024 * 1024 // 1MB chunks
  const totalChunks = Math.ceil(file.size / chunkSize)
  const maxRetries = 3
  const timeout = 60000 // 60 seconds

  const uploadChunk = async (
    chunk: Blob,
    chunkNumber: number,
    formData: FormData,
    uploadSessionId: string | null = null,
    attempt = 0
  ): Promise<string | null> => {
    try {
      if (uploadSessionId) {
        formData.append('upload_session_id', uploadSessionId)
      }

      const response = await axios.post<UploadResponse>(
        `${ApiBase}/${endpoint}?mode=${mode}`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
            ...getSentryHeaders(),
          },
          timeout,
        }
      )

      if (chunkNumber === totalChunks - 1) {
        onComplete?.(response.data)
        return response.data?.upload_session_id ?? null
      }
      return response.data?.upload_session_id ?? null
    } catch (error: unknown) {
      if (attempt >= maxRetries) {
        throw error
      }
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * Math.pow(2, attempt))
      )
      return uploadChunk(
        chunk,
        chunkNumber,
        formData,
        uploadSessionId,
        attempt + 1
      )
    }
  }

  let uploadSessionId = null

  for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
    const start = chunkNumber * chunkSize
    const end = Math.min(start + chunkSize, file.size)
    const chunk = file.slice(start, end)

    const formData = new FormData()
    formData.append('file', chunk)
    formData.append('chunk_number', chunkNumber.toString())
    formData.append('total_chunks', totalChunks.toString())
    formData.append('file_name', file.name)
    if (questionId) {
      formData.append('question', questionId.toString())
    }
    formData.append('response', responseId)
    if (inputFieldId) {
      formData.append('input_field', inputFieldId)
    }
    // Add any extra form data
    Object.entries(extraFormData).forEach(([key, value]) => {
      formData.append(key, value)
    })

    const result = await uploadChunk(
      chunk,
      chunkNumber,
      formData,
      uploadSessionId
    )

    if (chunkNumber === 0) {
      uploadSessionId = result
    }
  }
}
